import get from "lodash/get";
import { FIELD_STATE_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { UnsavedEdit } from "PFApp/stores/manage_roles/manage_roles_store.types";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { Id } from "PFCore/types";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { findRoleFormPath } from "../manage_roles.utils";
import {
  CONNECTED_FIELD_GROUPS,
  getEmptyErroredEdits,
  getErrorLeafFieldName
} from "./use_unsaved_edits_sync.utils";

type UseRevertEmptyDraftFields = {
  growlId: string;
};

export const useRevertEmptyDraftFields = ({ growlId }: UseRevertEmptyDraftFields) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });
  const { getValues, setValue } = useFormContext<ManageRolesMultiForm>();
  const { showGrowl } = useGrowlStoreActions();

  const revertEmptyDraftFields = useCallback(
    (roleId: Id, errorFieldNames: string[], edits: UnsavedEdit[], roleValues: ManageRolesFormType) => {
      if (get(roleValues, FIELD_STATE_KEY) !== "draft") {
        return { revertedKeys: [] as string[], remainingErrorFieldNames: errorFieldNames };
      }

      const path = findRoleFormPath(getValues(), roleId);

      if (!path) {
        return { revertedKeys: [] as string[], remainingErrorFieldNames: errorFieldNames };
      }

      const rolePath = `subtemplates.${path.subtemplateIndex}.roles.${path.roleIndex}`;
      const currentRoleValues = get(getValues(), rolePath) as ManageRolesFormType;
      const emptyErroredEdits = getEmptyErroredEdits(errorFieldNames, edits, currentRoleValues);

      if (emptyErroredEdits.length === 0) {
        return { revertedKeys: [] as string[], remainingErrorFieldNames: errorFieldNames };
      }

      // Connected fields must be reverted as a whole, otherwise the connected field's pending edit
      // would still be sent on retry, applying half of a rejected change.
      const connectedEdits = edits.filter(
        (edit) =>
          !emptyErroredEdits.includes(edit) &&
          CONNECTED_FIELD_GROUPS.some(
            (group) =>
              group.includes(edit.fieldName) &&
              emptyErroredEdits.some((erroredEdit) => group.includes(erroredEdit.fieldName))
          )
      );
      const editsToRevert = [...emptyErroredEdits, ...connectedEdits];

      editsToRevert.forEach((edit) => {
        setValue(`${rolePath}.${edit.fieldName}` as any, edit.previousValue as any);
      });

      const revertedLeafNames = new Set(editsToRevert.map((edit) => getErrorLeafFieldName(edit.fieldName)));
      const revertedKeys = editsToRevert.map((edit) => `${roleId}.${edit.fieldName}`);

      showGrowl({
        message: t("requiredFieldRevert"),
        kind: "error",
        id: growlId,
        hidePreviousByTypeAndId: true
      });

      return {
        revertedKeys,
        remainingErrorFieldNames: errorFieldNames.filter((fieldName) => !revertedLeafNames.has(fieldName))
      };
    },
    [getValues, growlId, setValue, showGrowl, t]
  );

  return { revertEmptyDraftFields };
};
